import { oauthAuth } from "../standardAuthPatterns";
import { defineConnector } from "../types";
import logo from "./logo.svg";

export const xero = defineConnector({
  identifier: "xero",
  name: "Xero",
  tagLine: "Cloud accounting software",
  keywords: "accounting bookkeeping invoices bills general ledger",
  logo,
  protocol: "http",
  tags: ["productivity", "data"],
  defaultUrlPattern: () => "https://api\\.xero\\.com/.*",
  auth: oauthAuth,
  steps: [
    {
      title: "Create a Xero app",
      items: [
        {
          kind: "markdown",
          content:
            "Visit the [Xero Developer Portal](https://developer.xero.com/app/manage) and click **‘New app’**.\n\nSelect **‘Web app’** as the app type and fill in the required details.",
        },
      ],
    },
    {
      title: "Configure the redirect URI",
      items: [
        {
          kind: "markdown",
          content: "In your app’s settings, add the following redirect URI:",
        },
        {
          kind: "copyable",
          label: "Redirect URI",
          value: "{API_URL}/connected-apps/oauth/xero/callback",
        },
      ],
    },
    {
      title: "Copy your app credentials",
      items: [
        {
          kind: "markdown",
          content:
            "From your app’s configuration page, copy the **Client ID** and generate a **Client secret**.",
        },
        {
          kind: "input",
          label: "Client ID",
          placeholder: "Paste your Client ID",
          required: true,
          target: { kind: "oauthConnection", field: "clientId" },
        },
        {
          kind: "input",
          label: "Client secret",
          placeholder: "Paste your Client secret",
          required: true,
          target: { kind: "oauthConnection", field: "clientSecret" },
        },
      ],
    },
    {
      title: "Configure scopes",
      items: [
        {
          kind: "markdown",
          content:
            "Enter the scopes you need for your integration. Common scopes include `openid`, `profile`, `email`, `accounting.transactions`, `accounting.contacts`, `accounting.settings`.\n\nSee the [Xero OAuth 2.0 scopes documentation](https://developer.xero.com/documentation/guides/oauth2/scopes/) for the full list.",
        },
        {
          kind: "input",
          label: "Scopes",
          placeholder:
            "openid profile email offline_access accounting.transactions",
          helperText:
            "Space-separated list of permission scopes. Include offline_access for refresh tokens.",
          target: { kind: "oauthConnection", field: "scopes" },
        },
      ],
    },
    {
      title: "Authorize your Xero account",
      items: [
        {
          kind: "markdown",
          content: "Click below to authorize your Xero account.",
        },
        {
          kind: "oauth",
          dependsOn: [
            { kind: "oauthConnection", field: "clientId" },
            { kind: "oauthConnection", field: "clientSecret" },
          ],
        },
      ],
    },
  ],
  oauth: {
    grantType: () => "authorization_code",
    authorizationEndpoint: () =>
      "https://login.xero.com/identity/connect/authorize",
    tokenEndpoint: () => "https://identity.xero.com/connect/token",
    tokenEndpointAuthMethod: () => "client_secret_post",
    clientCredentialsFrom: "db",
    scopes: () => [],
  },
  connectionTest: {
    protocol: "http",
    request: () => "https://api.xero.com/connections",
  },
});
